import React, { useState, useEffect, useRef } from "react";
import { Card } from "../ui/Card";
import { Button } from "../ui/Button";
import apiService from "../../services/ApiService";
import { NetworkError, ApiError } from "../../types/api";

interface ScannedBarcode {
  id: string;
  barcode: string;
  scanned_at: string;
  operator_username: string;
  operator_full_name: string;
  shift_id: string;
  shift_name: string;
  device_id: string;
  created_at: string;
}

interface BitboxBlock {
  id: string;
  status: "pass" | "fail";
  shift_id: string;
  scanned_barcode: ScannedBarcode[];
  created_at: string;
}

export const InvalidSetsPanel: React.FC = () => {
  const [invalidSets, setInvalidSets] = useState<BitboxBlock[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isPolling, setIsPolling] = useState(false);
  const pollingInterval = useRef<NodeJS.Timeout | null>(null);
  const POLLING_INTERVAL = 15000; // 15 seconds

  useEffect(() => {
    loadInvalidSets();
    startPolling();

    // Update current time every second for accurate "last updated" display
    const timeInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => {
      stopPolling();
      clearInterval(timeInterval);
    };
  }, []);

  const startPolling = () => {
    if (pollingInterval.current) return;

    setIsPolling(true);
    pollingInterval.current = setInterval(() => {
      if (apiService.isAuthenticated()) {
        loadInvalidSets(true); // Silent reload
      }
    }, POLLING_INTERVAL);
  };

  const stopPolling = () => {
    if (pollingInterval.current) {
      clearInterval(pollingInterval.current);
      pollingInterval.current = null;
    }
    setIsPolling(false);
  };

  const loadInvalidSets = async (silent = false) => {
    if (!silent) {
      setIsLoading(true);
    }
    setError("");

    try {
      if (!apiService.isAuthenticated()) {
        setError("Authentication required. Please log in again.");
        return;
      }

      // Fetch only "fail" status bitbox blocks
      const response = await apiService.getBitboxBlocks({
        status: "fail",
        limit: 100,
      });

      if (response && response.data) {
        setInvalidSets(response.data);
        setLastUpdated(new Date());
      } else {
        setError("Invalid response format from server");
      }
    } catch (error) {
      if (error instanceof NetworkError) {
        setError("Network error. Please check your connection and try again.");
      } else if (error instanceof ApiError) {
        setError(`API Error: ${error.message}`);
      } else {
        setError("An unexpected error occurred");
        console.error("Load invalid sets error:", error);
      }

      if (!silent) {
        stopPolling();
      }
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  };

  const refreshData = () => {
    if (apiService.isAuthenticated()) {
      loadInvalidSets();
    }
  };

  const formatLastUpdated = (date: Date | null) => {
    if (!date) return "Never";
    const now = currentTime;
    const diffMs = now.getTime() - date.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);

    if (diffSeconds < 5) {
      return "Just now";
    } else if (diffSeconds < 60) {
      return `${diffSeconds}s ago`;
    } else if (diffSeconds < 3600) {
      return `${Math.floor(diffSeconds / 60)}m ago`;
    } else {
      return date.toLocaleTimeString();
    }
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  // Helper to get operator name from scanned barcodes
  const getOperatorName = (scannedBarcodes: ScannedBarcode[]) => {
    return scannedBarcodes[0]?.operator_full_name || "Unknown";
  };

  // Helper to get shift name from scanned barcodes
  const getShiftName = (scannedBarcodes: ScannedBarcode[]) => {
    return scannedBarcodes[0]?.shift_name || "Unknown";
  };

  // Helper to sort barcodes by device for consistent display
  const getSortedBarcodes = (scannedBarcodes: ScannedBarcode[]) => {
    return [...scannedBarcodes].sort((a, b) => {
      // Extract camera number for sorting (Camera 1, Camera 2, Camera 3)
      const getCameraNumber = (deviceId: string) => {
        const match = deviceId.match(/Camera (\d+)/);
        return match ? parseInt(match[1]) : 999;
      };
      return getCameraNumber(a.device_id) - getCameraNumber(b.device_id);
    });
  };

  // Helper to detect mismatches from actual barcode data
  const getMismatchText = (scannedBarcodes: ScannedBarcode[]) => {
    const sortedBarcodes = getSortedBarcodes(scannedBarcodes);
    const mismatches = [];

    if (sortedBarcodes.length >= 3) {
      const [cam1, cam2, cam3] = sortedBarcodes;
      if (cam1.barcode !== cam2.barcode) mismatches.push("Cam 1 ≠ Cam 2");
      if (cam1.barcode !== cam3.barcode) mismatches.push("Cam 1 ≠ Cam 3");
      if (cam2.barcode !== cam3.barcode) mismatches.push("Cam 2 ≠ Cam 3");
    }

    return mismatches.length > 0
      ? mismatches.join(", ")
      : "Barcode mismatch detected";
  };

  return (
    <Card padding="md" className="h-full overflow-auto">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-red-800">
          Invalid Blocks ({invalidSets.length})
        </h2>

        <div className="flex items-center gap-3 text-sm">
          {/* Last updated indicator */}
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <div
              className={`w-2 h-2 rounded-full ${
                isPolling ? "bg-red-500 animate-pulse" : "bg-gray-400"
              }`}
            />
            <span>
              {isPolling ? "Live" : "Manual"} • Last updated:{" "}
              {formatLastUpdated(lastUpdated)}
            </span>
          </div>

          <Button
            variant="secondary"
            size="sm"
            onClick={refreshData}
            disabled={isLoading || !apiService.isAuthenticated()}
          >
            {isLoading ? "Loading..." : "Refresh"}
          </Button>
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <div className="flex justify-between items-center">
            <div className="text-sm text-yellow-800">{error}</div>
            {!isPolling && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setError("");
                  startPolling();
                }}
                className="text-xs"
              >
                Retry Live Updates
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Polling indicator */}
      {isPolling && (
        <div className="mb-4 text-xs text-red-600 bg-red-50 px-2 py-1 rounded flex items-center gap-1">
          <div className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse"></div>
          Auto-updating every {POLLING_INTERVAL / 1000}s
        </div>
      )}

      <div className="space-y-2 max-h-[500px] overflow-auto">
        {invalidSets.length > 0 ? (
          invalidSets.map((set) => {
            const sortedBarcodes = getSortedBarcodes(set.scanned_barcode);
            const operatorName = getOperatorName(set.scanned_barcode);
            const shiftName = getShiftName(set.scanned_barcode);
            const mismatchText = getMismatchText(set.scanned_barcode);

            return (
              <div
                key={set.id}
                className="p-3 border border-red-200 rounded bg-red-50 hover:bg-red-100 transition-colors"
              >
                <div className="flex justify-between items-start mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <span className="font-medium text-red-800">FAIL</span>
                  </div>
                  <span className="text-xs text-gray-500">
                    {formatDateTime(set.created_at)}
                  </span>
                </div>

                <div className="text-sm space-y-1">
                  <div className="font-mono text-xs bg-white p-2 rounded border">
                    {sortedBarcodes.map((scan) => (
                      <div key={scan.id}>
                        {scan.device_id}:{" "}
                        <span className="text-red-600">{scan.barcode}</span>
                      </div>
                    ))}
                  </div>

                  <div className="text-xs text-red-600 bg-red-100 p-2 rounded">
                    Mismatch: {mismatchText}
                  </div>

                  <div className="flex justify-between text-xs text-gray-600 mt-2">
                    <span>Operator: {operatorName}</span>
                    <span>Shift: {shiftName.split(" - ")[1] || shiftName}</span>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center py-8">
            <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
              <svg
                className="w-8 h-8 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <p className="text-gray-500 mb-4">No invalid sets recorded yet</p>
            {apiService.isAuthenticated() && (
              <Button
                variant="primary"
                onClick={refreshData}
                disabled={isLoading}
              >
                {isLoading ? "Loading..." : "Load from Server"}
              </Button>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};
